// Bestellung und Partien in der Akte (E3c, S3 und S4) – reine Logik, ohne
// React: was je Lieferant zusammen bestellt wird, der Text der Mails aus den
// Vorlagen der Firma (Kern, mailFuellen), der `mailto:`-Link (Weg A,
// DECISIONS.md 2.11.), die Namen der Dateien zum Anhängen.
//
// G20 (Gernot, 2.11.): „Übersichtlich, bevor geklickt wird“ – deshalb
// entsteht hier alles, was die Karte VOR dem Klick zeigt: „Unterkonstruktion
// + Module → Gautzsch, Lieferung Fr., 4. Dez.“ und der Text der Mail. Der
// Klick öffnet dann nur noch, was schon zu sehen war.
//
// Datenschutz (Prüfung des Plans E3c, Befund 13): In die Mails kommt nur, was
// die Vorlage nennt – Adresse, Termin, Mengen, Fabrikat und bei der
// Dachpartie der Name des Kunden (Gernot, 2.11.); nie Telefon, Mail oder
// Notizen des Kunden. Der Link und der Text bleiben im Browser: Sie gehen nie
// an den Server, nie in ein Protokoll und nie an die Fehlerwache
// (`ablaufFehlerFuerWache`).

import {
  ABLAUF_GRENZEN,
  BESTELL_TEIL_NAMEN,
  BESTELL_TEILE,
  mailFuellen,
  nurStellenImCode,
  type AblaufBestellung,
  type AblaufEinsatz,
  type MailVorlage,
  type PartnerZeile,
} from "@happy-energy/kern";
import { adresseZeigen } from "../anzeige.ts";
import { dateiname, type DateiRahmen } from "../freigabe/dachplanung.ts";

// --- Tage und Zeiten in Worten -----------------------------------------------------------------

function alsDatum(tag: string): Date | null {
  const [j, m, t] = tag.split("-").map(Number);
  return j && m && t ? new Date(j, m - 1, t) : null;
}

/** „Fr., 4.12.2026“ – so steht ein Tag in den Mails (MAIL_PLATZHALTER: „Mi., 28.10.2026“). */
export function tagInDerMail(tag: string): string {
  const d = alsDatum(tag);
  if (!d) return tag;
  const wochentag = d.toLocaleDateString("de-AT", { weekday: "short" }).replace(/\.?$/, ".");
  return `${wochentag}, ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

/** „Fr., 4. Dez.“ – kurz, für die Karte. */
export function tagKurz(tag: string): string {
  const d = alsDatum(tag);
  return d ? d.toLocaleDateString("de-AT", { weekday: "short", day: "numeric", month: "short" }) : tag;
}

/** „7:30 Uhr“ aus „07:30“. */
export function uhrzeitText(uhrzeit: string): string {
  const [h, m] = uhrzeit.split(":");
  return `${Number(h)}:${m ?? "00"} Uhr`;
}

/** „Mo., 7.12.2026, 7:30 Uhr“ – der Termin der Dachpartie in der Mail. */
export function terminInDerMail(tag: string, uhrzeit: string | null): string {
  return uhrzeit ? `${tagInDerMail(tag)}, ${uhrzeitText(uhrzeit)}` : tagInDerMail(tag);
}

/** „26 Module, Trina Vertex S+“ – null, solange Anzahl oder Fabrikat fehlen. */
export function moduleText(m: { anzahl: number | null; fabrikat: string | null }): string | null {
  if (m.anzahl === null || !m.fabrikat) return null;
  return `${m.anzahl} ${m.anzahl === 1 ? "Modul" : "Module"}, ${m.fabrikat}`;
}

// --- Empfänger ------------------------------------------------------------------------------------

/** Die Ansprechpartner, an die eine Mail gehen kann – die mit Mail-Adresse. */
export function mitMailAdresse(p: Pick<PartnerZeile, "ansprechpartner">): PartnerZeile["ansprechpartner"] {
  return p.ansprechpartner.filter((a) => a.email !== null && a.email !== "");
}

/**
 * Die Mail geht an EINEN Ansprechpartner – den angetippten (Gernot,
 * Augentest E3c, 3.11.2026: „muss ich einfach den Ansprechpartner auswählen,
 * den ich haben will“). `nr` zählt unter denen mit Mail; vorgewählt ist der
 * erste, auch wenn `nr` nicht mehr passt (die Liste wurde inzwischen kürzer).
 * Mit höchstens einem bleibt alles, wie es ist.
 */
export function nurAnsprechpartner<P extends Pick<PartnerZeile, "ansprechpartner">>(p: P, nr: number): P {
  const moeglich = mitMailAdresse(p);
  if (moeglich.length < 2) return p;
  return { ...p, ansprechpartner: [moeglich[nr] ?? moeglich[0]!] };
}

/** Die Mail-Adressen eines Lieferanten oder einer Partie – alle Ansprechpartner mit Mail. */
export function empfaenger(p: Pick<PartnerZeile, "ansprechpartner">): string[] {
  return p.ansprechpartner.map((a) => a.email).filter((m): m is string => m !== null && m !== "");
}

/** Die Anrede: die Ansprechpartner mit Mail („Clara Probe und David Test“) – ohne Mail alle, ohne Namen „zusammen“. */
export function anredeNamen(p: Pick<PartnerZeile, "ansprechpartner">): string {
  const mitMail = p.ansprechpartner.filter((a) => a.email);
  const namen = (mitMail.length > 0 ? mitMail : p.ansprechpartner).map((a) => a.name.trim()).filter(Boolean);
  if (namen.length === 0) return "zusammen";
  if (namen.length === 1) return namen[0]!;
  return `${namen.slice(0, -1).join(", ")} und ${namen.at(-1)!}`;
}

// --- Die Bestellung je Lieferant ----------------------------------------------------------------

/** Die offenen Teile eines Lieferanten – sie gehen in EINE Mail (H27: gleiche Lieferanten → eine Mail). */
export interface BestellGruppe {
  lieferant: PartnerZeile;
  teile: AblaufBestellung[];
}

/** Die Reihenfolge der Teile, wie die Karte sie zeigt. */
const nachTeil = (a: AblaufBestellung, b: AblaufBestellung) => BESTELL_TEILE.indexOf(a.teil) - BESTELL_TEILE.indexOf(b.teil);

/**
 * Die offenen (noch nicht bestellten) Teile, je Lieferant eine Gruppe – in
 * der Reihenfolge des ersten Teils. Ein Lieferant, den die Seite nicht kennt
 * (die Liste ist alt), fehlt; die Karte lädt dann neu.
 */
export function bestellGruppen(bestellungen: readonly AblaufBestellung[], partner: readonly PartnerZeile[]): BestellGruppe[] {
  const gruppen: BestellGruppe[] = [];
  for (const b of [...bestellungen].sort(nachTeil)) {
    if (b.erledigtAm !== null || b.lieferantId === null) continue;
    const vorhanden = gruppen.find((g) => g.lieferant.id === b.lieferantId);
    if (vorhanden) vorhanden.teile.push(b);
    else {
      const lieferant = partner.find((p) => p.id === b.lieferantId);
      if (lieferant) gruppen.push({ lieferant, teile: [b] });
    }
  }
  return gruppen;
}

/** Die Wunschliefertermine einer Gruppe – verschieden, in Reihenfolge; null zählt nicht. */
function liefertermine(g: BestellGruppe): string[] {
  return [...new Set(g.teile.map((t) => t.wunschliefertermin).filter((t): t is string => t !== null))].sort();
}

/** G20: „Unterkonstruktion + Module → Gautzsch, Lieferung Fr., 4. Dez.“ */
export function gruppenSatz(g: BestellGruppe): string {
  const tage = liefertermine(g);
  const lieferung = tage.length === 0 ? "Liefertermin fehlt" : `Lieferung ${tage.map(tagKurz).join(" und ")}`;
  return `${g.teile.map((t) => BESTELL_TEIL_NAMEN[t.teil]).join(" + ")} → ${g.lieferant.name}, ${lieferung}`;
}

/** Ein Teil in der Mail – eine Zeile von [Teile]. */
function teilZeile(t: AblaufBestellung, module: string | null, mitTag: boolean): string {
  const was =
    t.teil === "unterkonstruktion" ? "Unterkonstruktion laut Unterkonstruktionsliste (im Anhang)" : t.teil === "module" ? (module ?? "Module") : BESTELL_TEIL_NAMEN[t.teil];
  return `- ${was}${mitTag && t.wunschliefertermin ? ` – Lieferung ${tagInDerMail(t.wunschliefertermin)}` : ""}`;
}

/** Eine Mail, fertig gefüllt – und was noch fehlt, damit sie hinausgehen kann (leer: nichts). */
export interface FertigeMail {
  an: string[];
  betreff: string;
  text: string;
  /** Was fehlt, in Worten – „der Liefertermin“, „Anzahl und Fabrikat der Module“. */
  fehlt: string[];
}

/** Was jede Mail kennt. */
export interface MailUmfeld {
  vorlage: MailVorlage;
  objekt: { strasse: string; plz: string; ort: string };
  firma: string;
  module: { anzahl: number | null; fabrikat: string | null };
}

/** Die Bestellung an einen Lieferanten – aus der Vorlage der Firma (Kern, mailFuellen); was fehlt, bleibt als [Platzhalter] stehen. */
export function bestellMail(g: BestellGruppe, u: MailUmfeld): FertigeMail {
  const tage = liefertermine(g);
  const module = moduleText(u.module);
  const fehlt: string[] = [];
  if (g.teile.some((t) => t.wunschliefertermin === null)) fehlt.push("der Liefertermin (er kommt aus dem Montagetermin)");
  if (g.teile.some((t) => t.teil === "module") && module === null) fehlt.push("Anzahl und Fabrikat der Module – oben eintragen, dann „Speichern“");
  const werte: Record<string, string> = {
    Ansprechpartner: anredeNamen(g.lieferant),
    Adresse: adresseZeigen(u.objekt),
    Teile: g.teile.map((t) => teilZeile(t, module, tage.length > 1)).join("\n"),
    Firma: u.firma,
  };
  if (tage.length > 0) werte["Liefertermin"] = tage.map(tagInDerMail).join(" und ");
  return { an: empfaenger(g.lieferant), betreff: mailFuellen(u.vorlage.betreff, werte), text: mailFuellen(u.vorlage.text, werte), fehlt };
}

/** Die Mail an die Dachpartie – mit dem Namen des Kunden (er läutet an der Tür), nie seinem Telefon. */
export function dachMail(e: Pick<AblaufEinsatz, "tag" | "uhrzeit">, partie: PartnerZeile, u: MailUmfeld & { kunde: string }): FertigeMail {
  const module = moduleText(u.module);
  const fehlt: string[] = [];
  if (e.tag === null) fehlt.push("der Tag");
  if (module === null) fehlt.push("Anzahl und Fabrikat der Module (in der Bestellung)");
  const werte: Record<string, string> = {
    Ansprechpartner: anredeNamen(partie),
    Kunde: u.kunde,
    Adresse: adresseZeigen(u.objekt),
    Firma: u.firma,
  };
  if (e.tag !== null) werte["Termin"] = terminInDerMail(e.tag, e.uhrzeit);
  if (module !== null) werte["Module"] = module;
  return { an: empfaenger(partie), betreff: mailFuellen(u.vorlage.betreff, werte), text: mailFuellen(u.vorlage.text, werte), fehlt };
}

// --- Der Link ins Mailprogramm ------------------------------------------------------------------

/**
 * So lang darf der ganze `mailto:`-Link höchstens werden – Outlook unter
 * Windows kappt um 2.000 Zeichen (ABLAUF_GRENZEN.mailtext, Prüfung des Plans,
 * Befund 13 d). Wird er länger, öffnet der Link die Mail ohne Text, und die
 * Karte bietet „Text kopieren“ an.
 */
export const MAILTO_HOECHSTENS = 2000;

/**
 * Der `mailto:`-Link: Empfänger (mit Komma getrennt), Betreff, Text –
 * Zeilen als CRLF (RFC 6068). `mitText` false: Der Text passte nicht hinein
 * (länger als ABLAUF_GRENZEN.mailtext oder der Link zu lang) – dann ohne.
 */
export function mailtoLink(an: readonly string[], betreff: string, text: string): { href: string; mitText: boolean } {
  const adressen = an.map((a) => encodeURIComponent(a).replace(/%40/g, "@")).join(",");
  const ohneText = `mailto:${adressen}?subject=${encodeURIComponent(betreff)}`;
  const mit = `${ohneText}&body=${encodeURIComponent(text.replace(/\r?\n/g, "\r\n"))}`;
  if (text.length <= ABLAUF_GRENZEN.mailtext && mit.length <= MAILTO_HOECHSTENS) return { href: mit, mitText: true };
  return { href: ohneText, mitText: false };
}

// --- Die Dateien zum Anhängen -------------------------------------------------------------------

/** „2026-10-22_Unterkonstruktionsliste K2_Familie Maier_Happy Energy Sun.xlsx“ – wie die anderen Dateien des CRM (O76). */
export function k2ExcelDateiname(kundeName: string, rahmen: DateiRahmen): string {
  return `${dateiname([rahmen.tag, "Unterkonstruktionsliste K2", kundeName, rahmen.produkt])}.xlsx`;
}

/** „2026-10-22_Dach-Unterlage_Familie Maier_Happy Energy Sun“ – ohne „.pdf“ (der Titel im PDF). */
export function dachUnterlageName(kundeName: string, rahmen: DateiRahmen): string {
  return dateiname([rahmen.tag, "Dach-Unterlage", kundeName, rahmen.produkt]);
}

// --- Die Fehlerwache -------------------------------------------------------------------------------

/**
 * Ein Fehler aus den Karten Bestellung, Dachpartie, Bodenpartie oder der
 * Dach-Unterlage für die Fehlerwache – ohne Kundendaten (CLAUDE.md:
 * „Alarme enthalten nie Kundendaten“; Prüfung des Plans, Befund 13 c): Die
 * Meldung ist fest, mit geht nur die Art des Fehlers und die Stellen im Code.
 * Die erste Zeile des Stapels trägt die Meldung des Fehlers – und damit
 * womöglich eine Adresse oder einen `mailto:`-Link – und bleibt weg; ebenso
 * jede Zeile, die nicht wie eine Stelle im Code aussieht. Seit dem Gutachten
 * vom 3.11.2026 (Befund 4) ist diese Regel im Kern (nurStellenImCode) und
 * gilt für jede Meldung der Apps und des Servers.
 */
export function ablaufFehlerFuerWache(fehler: unknown, wo: "Bestellung" | "Dachpartie" | "Bodenpartie" | "Dach-Unterlage"): Error {
  const f = fehler instanceof Error ? fehler : new Error("unbekannt");
  const meldung = new Error(`Fehler in der Karte ${wo}`);
  meldung.name = /^[A-Za-z]{1,60}$/.test(f.name) ? f.name : "Error";
  const stellen = nurStellenImCode(f.stack, f);
  meldung.stack = [`${meldung.name}: ${meldung.message}`, ...(stellen ? [stellen] : [])].join("\n");
  return meldung;
}
